import { positionKey } from './totals';

const VERSION = 1;
const DECIMAL = /^-?\d+(\.\d+)?$/;

const storageKey = (sub) => `taxPlanner:${sub}`;

const validate = (input, { allowNegative }) => {
  const value = typeof input === 'string' ? input.trim() : '';
  if (!DECIMAL.test(value)) return { ok: false, reason: 'NOT_A_NUMBER' };
  if (!allowNegative && value.startsWith('-')) return { ok: false, reason: 'NEGATIVE' };
  const [intPart, fraction = ''] = value.replace('-', '').split('.');
  if (fraction.length > 2) return { ok: false, reason: 'TOO_MANY_DECIMALS' };
  if (intPart.replace(/^0+(?=\d)/, '').length > 10) return { ok: false, reason: 'OUT_OF_RANGE' };
  return { ok: true, value };
};

export const validateLoss = (input) => validate(input, { allowNegative: false });
export const validateTarget = (input) => validate(input, { allowNegative: true });

const isMark = (m) =>
  !!m &&
  typeof m === 'object' &&
  typeof m.investingAccountId === 'string' &&
  typeof m.ticker === 'string' &&
  typeof m.currency === 'string';

const sameMark = (a, b) =>
  a.investingAccountId === b.investingAccountId && a.ticker === b.ticker && a.currency === b.currency;

const emptyRecord = () => ({ version: VERSION, previousPeriodLoss: null, targetResult: '0', strategic: [] });

// No sub, no storage (AC-31, A-12): the parameters then live in memory only and nothing is written under a shared key.
const getStore = (sub) => {
  if (!sub) return null;
  try {
    return window.localStorage || null;
  } catch (e) {
    return null;
  }
};

// Reads the record, validating every field independently; never throws.
const load = (sub) => {
  const record = emptyRecord();
  try {
    const store = getStore(sub);
    if (!store) return record;
    const parsed = JSON.parse(store.getItem(storageKey(sub)));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return record;
    const loss = parsed.previousPeriodLoss;
    if (loss && typeof loss.taxYear === 'number' && validateLoss(loss.value).ok) {
      record.previousPeriodLoss = { taxYear: loss.taxYear, value: loss.value.trim() };
    }
    if (typeof parsed.targetResult === 'string' && validateTarget(parsed.targetResult).ok) {
      record.targetResult = parsed.targetResult.trim();
    }
    if (Array.isArray(parsed.strategic)) {
      record.strategic = parsed.strategic.filter(isMark).map(({ investingAccountId, ticker, currency }) => ({
        investingAccountId,
        ticker,
        currency,
      }));
    }
  } catch (e) {
    return emptyRecord();
  }
  return record;
};

const save = (sub, record) => {
  try {
    const store = getStore(sub);
    if (!store) return false;
    store.setItem(storageKey(sub), JSON.stringify(record));
    return true;
  } catch (e) {
    return false;
  }
};

export const readParams = (sub, taxYear) => {
  const record = load(sub);
  const loss = record.previousPeriodLoss;
  return {
    loss: loss && loss.taxYear === taxYear ? loss.value : '0',
    target: record.targetResult,
    strategic: new Set(record.strategic.map(positionKey)),
  };
};

export const writeLoss = (sub, taxYear, input) => {
  const res = validateLoss(input);
  if (!res.ok) return { ok: false, reason: res.reason, persisted: false };
  const record = load(sub);
  record.previousPeriodLoss = { taxYear, value: res.value };
  return { ok: true, persisted: save(sub, record) };
};

export const writeTarget = (sub, input) => {
  const res = validateTarget(input);
  if (!res.ok) return { ok: false, reason: res.reason, persisted: false };
  const record = load(sub);
  record.targetResult = res.value;
  return { ok: true, persisted: save(sub, record) };
};

export const addStrategic = (sub, mark) => {
  const record = load(sub);
  if (!record.strategic.some((m) => sameMark(m, mark))) {
    const { investingAccountId, ticker, currency } = mark;
    record.strategic.push({ investingAccountId, ticker, currency });
  }
  return { persisted: save(sub, record) };
};

export const removeStrategic = (sub, mark) => {
  const record = load(sub);
  record.strategic = record.strategic.filter((m) => !sameMark(m, mark));
  return { persisted: save(sub, record) };
};

export const pruneStrategic = (sub, snapshot) => {
  const participating = new Set(
    (snapshot?.accounts || []).filter((a) => a.participation === 'PARTICIPATING').map((a) => a.investingAccountId),
  );
  const positions = snapshot?.positions || [];
  const record = load(sub);
  record.strategic = record.strategic.filter(
    (m) => !participating.has(m.investingAccountId) || positions.some((p) => sameMark(p, m)),
  );
  return { persisted: save(sub, record) };
};
